import { type ToastOptions, useToastStore } from '@/components/Toaster/store'
import { useTr } from '@/composables/use-tr'
import { useAppStore } from '@/store/app-store'
import { useLoggerStore } from '@/store/logger'
import { AUTH_ERROR_TOAST_WEIGHT } from '@/store/notification-que/notifications-queue'

export type AuthMethod = 'google' | 'apple' | 'microsoft'

export type AuthResult = {
    authMethod: AuthMethod
    loginSource: string | undefined
    isSuccess: boolean
    errorMessage?: string
}

export function useAuthResult() {
    const loggerStore = useLoggerStore()
    const appStore = useAppStore()
    const toastStore = useToastStore()
    const tr = useTr()

    return function handleAuthResult({ authMethod, loginSource, isSuccess, errorMessage }: AuthResult) {
        loggerStore.logEvent({
            event: 'custom_event',
            eventName: isSuccess ? 'auth_success' : 'auth_failed',
            dimension1: loginSource,
            dimension2: authMethod,
            dimension3: errorMessage?.substring(0, 100),
        })

        if (isSuccess) {
            return
        }

        const errorTranslationKeyMap: Record<string, string> = {
            'User is banned': tr('socialLogins.errorUserBanned'),
        }
        const defaultError = tr('socialLogins.errorDefaultDescription')
        const translatedDescription = errorTranslationKeyMap[errorMessage || ''] || defaultError

        const params: ToastOptions = {
            type: 'error-message',
            trigger: 'auth-failed',
            title: tr('socialLogins.errorToastTitle'),
            description: appStore.isDebugMode ? errorMessage || '' : translatedDescription,
            showTimeoutMs: 40_000, // 40 sec
            weight: AUTH_ERROR_TOAST_WEIGHT,
        }

        toastStore.showToast(params)
    }
}
